import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import {
    GetTranslationsDto,
    IAccountingBasisDTO,
    IAccountingBasisExtendedDTO,
    ICurrencyExtendedDTO,
    IFinancialLineItemDTO,
    IFinancialLineItemExtendedDTO,
    IFinancialLineItemUpdateRequest,
    ILegalFormDTO,
    ILegalFormExtendedDTO,
    UpdateTranslationsDto,
} from '@modeso/types__aumico-lib-settings-be';
import Debug from 'debug';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { IEditData } from '../interfaces/edit-data.interface';
import { IFilterOptions } from '../interfaces/filter-options.interface';
import { SettingsTranslationResponse } from '../interfaces/settings-response.interface';
import { SettingsTranslationRequest } from '../interfaces/settings-translation-request.interface';
import { AumicoSettingsConfig, AumicoSettingsConfigService } from './../aumico-settings.config';

const debug = Debug('modeso:aumico-annualreport:AumicoSettingsService');
@Injectable({
    providedIn: 'root',
})
export class AumicoSettingsService {
    private server: string;
    // no slashes
    microserviceName: string;
    accountingBasisPath = '/accounting-basis';
    legalFormPath = '/legal-form';
    financialLineItemPath = '/financial-line-item';
    currencyPath = '/currency';
    translations = '/translations';
    fallback = '/fallback';

    constructor(
        private http: HttpClient,
        @Inject(AumicoSettingsConfigService) private config: AumicoSettingsConfig,
        private coreProvider: AumicoCoreProvider,
    ) {
        // this includes a forward slash in the end
        this.server = config.apiGateway;
        // no slashes
        this.microserviceName = config.serviceId;
    }

    public getAllAccountingBasis(filterData?: IFilterOptions) {
        let url = `${this.server}${this.microserviceName}${this.accountingBasisPath}`;
        if (filterData != null) {
            url = `${url}?countryISO31661Alpha2Code=${filterData.countryISO31661Alpha2Code}`;
        }
        this.coreProvider.dispatchBlockingRequest('getAllAccountingBasis');
        return this.http
            .get<IAccountingBasisExtendedDTO[]>(url, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllAccountingBasis')));
    }

    public createAccountingBasis(data: IAccountingBasisDTO): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.accountingBasisPath}`;
        this.coreProvider.dispatchBlockingRequest('createAccountingBasis');
        return this.http
            .post<void>(url, data, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createAccountingBasis')));
    }

    public updateAccountingBasis(
        data: IEditData<Pick<IAccountingBasisDTO, 'text' | 'countryISO31661Alpha2Codes'>>,
    ): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.accountingBasisPath}/${data.id}`;
        const httpOptions = this.getHttpOptions();
        this.coreProvider.dispatchBlockingRequest('updateAccountingBasis');
        return this.http
            .put<void>(url, data.payload, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateAccountingBasis')));
    }

    public getAllLegalForm(filterOptions?: IFilterOptions) {
        let url = `${this.server}${this.microserviceName}${this.legalFormPath}`;
        if (filterOptions && filterOptions.countryISO31661Alpha2Code) {
            url = `${url}?countryISO31661Alpha2Code=${filterOptions.countryISO31661Alpha2Code}`;
        }
        this.coreProvider.dispatchBlockingRequest('getAllLegalForm');
        return this.http
            .get<ILegalFormExtendedDTO[]>(url, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllLegalForm')));
    }

    public createLegalForm(data: ILegalFormDTO): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.legalFormPath}`;
        return this.http.post<void>(url, data, this.getHttpOptions());
    }

    public updateLegalForm(
        data: IEditData<Pick<ILegalFormDTO, 'text' | 'countryISO31661Alpha2Codes'>>,
    ): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.legalFormPath}/${data.id}`;
        const httpOptions = this.getHttpOptions();
        return this.http.put<void>(url, data.payload, httpOptions);
    }

    //#region FinancialLineItem

    public getAllFinancialLineItem() {
        const url = `${this.server}${this.microserviceName}${this.financialLineItemPath}`;
        this.coreProvider.dispatchBlockingRequest('getAllFinancialLineItem');
        return this.http
            .get<IFinancialLineItemExtendedDTO[]>(url, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllFinancialLineItem')));
    }

    public createFinancialLineItem(data: IFinancialLineItemDTO): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.financialLineItemPath}`;
        return this.http.post<void>(url, data, this.getHttpOptions());
    }

    public updateFinancialLineItem(data: IEditData<IFinancialLineItemUpdateRequest>): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.financialLineItemPath}/${data.id}`;
        const httpOptions = this.getHttpOptions();
        return this.http.put<void>(url, data.payload, httpOptions);
    }

    //#endregion FinancialLineItem

    //#region Currency

    public getAllCurrency() {
        const url = `${this.server}${this.microserviceName}${this.currencyPath}`;
        this.coreProvider.dispatchBlockingRequest('getAllCurrency');
        return this.http
            .get<ICurrencyExtendedDTO[]>(url, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllCurrency')));
    }

    //#endregion Currency

    public getTranslations(locales: string[], untranslated: boolean): Observable<GetTranslationsDto> {
        let url = `${this.server}${this.microserviceName}${this.translations}`;

        const queryParams = `locales[]=${locales.join('&locales[]=')}&untranslated=${untranslated}`;
        const urlWithParams = `${url}?${queryParams}`;

        this.coreProvider.dispatchBlockingRequest('getTranslations');
        return this.http
            .get<GetTranslationsDto>(urlWithParams, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getTranslations')));
    }

    public updateTranslations(translations: UpdateTranslationsDto): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.translations}`;
        this.coreProvider.dispatchBlockingRequest('updateTranslations');
        return this.http
            .patch<void>(url, translations, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateTranslations')));
    }

    public getSettingsTargetTranslations(
        translationRequest: SettingsTranslationRequest,
    ): Observable<SettingsTranslationResponse> {
        const url = `${this.server}${this.microserviceName}${this.translations}${this.fallback}`;
        this.coreProvider.dispatchBlockingRequest('getSettingsTargetTranslations');
        return this.http
            .post<SettingsTranslationResponse>(url, translationRequest, this.getHttpOptions())
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getSettingsTargetTranslations')));
    }

    private getHttpOptions() {
        return {
            headers: new HttpHeaders(this.config.httpOptions.headers),
        };
    }
}
